import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin } from "lucide-react";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { NewsCard, NewsCardSkeleton } from "@/components/shared/NewsCard";
import { DestinationCard, DestinationCardSkeleton } from "@/components/shared/DestinationCard";
import { VideoCard, VideoCardSkeleton } from "@/components/shared/VideoCard";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

export default function CholutecaPage() {
  const [content, setContent] = useState({ news: [], destinations: [], videos: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [newsRes, destRes, videosRes] = await Promise.all([
          axios.get(`${API}/public/news?location_scope=choluteca&limit=6`),
          axios.get(`${API}/public/destinations?location_scope=choluteca&limit=4`),
          axios.get(`${API}/public/videos?location_scope=choluteca&limit=4`),
        ]);
        setContent({
          news: newsRes.data.items,
          destinations: destRes.data.items,
          videos: videosRes.data.items,
        });
      } catch (error) {
        console.error("Error fetching content:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <PageHero
        section="choluteca"
        title="Choluteca"
        subtitle="Descubre el corazón del sur de Honduras. Tierra de historia, cultura, sabores únicos y paisajes impresionantes que te enamorarán."
        badge="Honduras"
        badgeIcon={MapPin}
      />

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl font-bold text-brand-text mb-6">
              El Departamento del Sur
            </h2>
            <p className="text-brand-textSecondary text-lg leading-relaxed">
              Choluteca es uno de los departamentos más importantes de Honduras, conocido por su rica historia, 
              su deliciosa gastronomía y sus hermosos paisajes. Desde el Puente de Choluteca hasta las playas 
              del Golfo de Fonseca, esta región ofrece experiencias únicas para todos los visitantes.
            </p>
          </div>
        </div>
      </section>

      {/* News */}
      <section className="py-16" data-testid="choluteca-news">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Noticias de Choluteca
            </h2>
            <Link to="/noticias?location=choluteca" className="flex items-center text-primary font-medium hover:text-secondary">
              Ver todas <ChevronRight className="ml-1 h-5 w-5" />
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => <NewsCardSkeleton key={i} />)}
            </div>
          ) : content.news.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.news.map((post) => (
                <NewsCard key={post.post_id} post={post} className="h-[300px]" />
              ))}
            </div>
          ) : (
            <p className="text-center py-12 text-gray-500">No hay noticias de Choluteca aún</p>
          )}
        </div>
      </section>

      {/* Destinations */}
      <section className="py-16 bg-white" data-testid="choluteca-destinations">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Destinos en Choluteca
            </h2>
            <Link to="/destinos?location=choluteca" className="flex items-center text-primary font-medium hover:text-secondary">
              Ver todos <ChevronRight className="ml-1 h-5 w-5" />
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => <DestinationCardSkeleton key={i} />)}
            </div>
          ) : content.destinations.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {content.destinations.map((post) => (
                <DestinationCard key={post.post_id} post={post} />
              ))}
            </div>
          ) : (
            <p className="text-center py-12 text-gray-500">No hay destinos publicados aún</p>
          )}
        </div>
      </section>

      {/* Videos */}
      {content.videos.length > 0 && (
        <section className="py-16 bg-[#0B0F14]" data-testid="choluteca-videos">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
                Videos de Choluteca
              </h2>
              <Link to="/videos?location=choluteca" className="flex items-center text-accent font-medium hover:text-white">
                Ver todos <ChevronRight className="ml-1 h-5 w-5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {content.videos.map((video) => (
                <VideoCard key={video.post_id} post={video} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
